import { CheckOutlined } from '@ant-design/icons';
import { Avatar, Input } from 'antd';
import { SearchIcon } from 'assets/Icons/AppointmentIcons';
import Modal from 'components/Modal';
import appointmentApis from 'features/appointment/services/apis';
import appointmentSelectors from 'features/appointment/services/selectors';
import { get } from 'lodash';
import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { IResponseDataBody } from 'services/response';
import { IStaffItemData } from 'services/shop/types/staff';
import {
  Container,
  Content,
  EmptyText,
  Footer,
  Header,
  SearchBar,
  StaffCard,
} from './styles';

type IModalStaffPickerProps = {};
type IOpenConfigs = {
  date: string;
  activeStaffId?: string;
  // Staff ids already added in the booking — hidden from the list to avoid duplicates.
  excludeStaffIds?: string[];
};
type IModalStaffPickerRef = {
  open: (configs: IOpenConfigs) => Promise<IStaffItemData | undefined>;
  fetchStaffs: (date: string) => void;
  close: () => void;
};
export const ModalStaffPickerRef = React.createRef<IModalStaffPickerRef>();
const ModalStaffPicker = forwardRef<IModalStaffPickerRef, IModalStaffPickerProps>((props, ref) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [textSearch, setTextSearch] = useState('');
  const [activeId, setActiveId] = useState('');
  const [excludeStaffIds, setExcludeStaffIds] = useState<string[]>([]);
  const [staffList, setStaffList] = useState<IStaffItemData[]>([]);
  const callbackResolve = useRef<(val?: IStaffItemData) => void>(() => undefined);
  // Per-date cache so re-opening shows results instantly while refreshing in the background.
  const staffCacheRef = useRef<Record<string, IStaffItemData[]>>({});
  const reqDateRef = useRef('');

  const staffsFiltered = useMemo(() => {
    const staffs = staffList.filter((o) => !o.anyBody && !excludeStaffIds.includes(o.id));
    const keyword = textSearch.trim().toLowerCase();
    if (!keyword) return staffs;
    return staffs.filter((o) => (o.nickName || '').toLowerCase().includes(keyword));
  }, [staffList, textSearch, excludeStaffIds]);

  const handleOpen: IModalStaffPickerRef['open'] = (configs) => {
    setActiveId(configs.activeStaffId || '');
    setExcludeStaffIds(configs.excludeStaffIds || []);
    setTextSearch('');
    reqDateRef.current = configs.date;
    // Show cached results immediately (instant, no blocking); refresh silently below.
    setStaffList(staffCacheRef.current[configs.date] || []);
    setOpen(true);
    fetchStaffs(configs.date);
    return new Promise((resolve) => {
      callbackResolve.current = resolve;
    });
  };

  const handleClose: IModalStaffPickerRef['close'] = () => {
    setOpen(false);
    callbackResolve.current(undefined);
  };

  // Fetches in the background — never blocks the UI; only applies if still the current date.
  const fetchStaffs = async (date: string) => {
    setLoading(true);
    try {
      const resData: IResponseDataBody<IStaffItemData[]> = await appointmentApis.getListStaffAvailableBooking(date || '');
      const data = resData.data.data || [];
      staffCacheRef.current[date] = data;
      if (reqDateRef.current === date) setStaffList(data);
    } catch (error) {
      // Keep whatever is already shown (cached/previous) on failure.
    } finally {
      if (reqDateRef.current === date) setLoading(false);
    }
  };

  const handleSelect = (staff: IStaffItemData) => {
    callbackResolve.current(staff);
    setOpen(false);
  };

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    fetchStaffs: fetchStaffs,
    close: handleClose,
  }));

  return (
    <Modal
      visible={open}
      width={'80vw'}
      onClose={handleClose}
      hiddenHeader
      hiddenFooter
      noneBodyStyle
      destroyOnClose
    >
      <Container>
        <Header>
          <span>Assign Staff</span>
        </Header>
        <SearchBar>
          <Input
            value={textSearch}
            onChange={(e) => setTextSearch(e.target.value)}
            placeholder="Search by Staff's Name"
            suffix={<SearchIcon fill="#fff" />}
          />
        </SearchBar>
        <Content>
          {staffsFiltered.map((staff) => (
            <StaffCard
              key={staff.id}
              type="button"
              data-active={staff.id === activeId}
              onClick={() => handleSelect(staff)}
            >
              {staff.id === activeId && (
                <span className="check">
                  <CheckOutlined />
                </span>
              )}
              <Avatar size={87} src={staff.urlImage}>
                {get(staff.nickName, [0], '')}
              </Avatar>
              <span className="name">{staff.nickName}</span>
            </StaffCard>
          ))}
          {!loading && !staffsFiltered.length && <EmptyText>No staff found</EmptyText>}
        </Content>
        <Footer>
          <button type="button" className="btn" onClick={handleClose}>
            <span>Close</span>
          </button>
        </Footer>
      </Container>
    </Modal>
  );
});

ModalStaffPicker.displayName = 'ModalStaffPicker';

export default ModalStaffPicker;
